import {
  faCircleInfo,
  faEye,
  faFlag,
  faFlask,
  faMicrochip,
  faPaintbrush,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BasicTableCell,
  BasicTableRow,
  Button,
  Modal,
} from '@orobix/orobix-fe-components'
import { useField } from 'formik'
import { filter, intersection, reject } from 'ramda'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStore } from '../../store/store'
import { getAnnotationTypeBy } from '../../types/annotation'
import { get } from '../../utils/api'
import { formatDate } from '../../utils/date'
import { getQueryString } from '../../utils/queryString'
import { persist, retrieve } from '../../utils/storage'
import AnnotationStatusTag from '../AnnotationStatusTag/AnnotationStatusTag'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal/DeleteConfirmationModal'
import ObservationItem from '../ObservationItem/ObservationItem'
import { getObservationsQueryParams } from '../Observations/getObservationsQueryParams'
import { ObservationsParams, ObservationsResult } from '../Observations/types'
import { ObservationsBrowser } from '../ObservationsBrowser/ObservationsBrowser'
import {
  ObservationsBrowserHelpers,
  ObservationsBrowserWidgets,
} from '../ObservationsBrowser/types'
import { useObservationsDelete } from '../ObservationsBrowser/useObservationsDelete'
import useIconRowStyles from '../ObservationsBrowser/Views/iconsRow.styles'
import { MenuItem } from '../ObservationsBrowser/Views/types'
import { ObservationsBrowserFilters } from '../ObservationsBrowserFilters/ObservationsBrowserFilters'
import ProductClassTag from '../ProductClassTag/ProductClassTag'
import SelectPercentageButtonModal from '../SelectPercentageButton/SelectPercentageButtonModal'
import SortModal from '../SortModal/SortModal'
import { DeselectAllButton } from './DeselectAllButton'
import { DownloadSelectedButton } from './DownloadSelectedButton'
import useStyles from './ObservationsSelectField.styles'
import { SelectAllButton } from './SelectAllButton'
import { Observation, ProductType } from './types'
import { titleUndeletableObservation } from './utils'

interface ObservationsSelectFieldProps {
  name: string
  productType: ProductType
  productId: number
  trainingId?: number
  excludeTrainingObservations?: boolean
  defaultFilters?: ObservationsParams
  fixedFilters?: ObservationsParams
  excludeClasses?: string[]
  useAvailabilityFilter?: boolean
  showDatasetManager?: boolean
  setShowDatasetManager?: React.Dispatch<React.SetStateAction<boolean>>
  dataset?: string[]
  onObservationsCreated?: (toAdd: number) => void
  inTraining?: boolean
  inTest?: boolean
  useWindowScroll?: boolean
  scrollContainerId?: string
  autoSelectFirst?: boolean
  initialSelectedObservations?: { id: number }[]
  isEditMode?: boolean
  inDatasetSection?: boolean
  onNonAnnotatedImagesInDataset?: (hasNonAnnotated: boolean) => void
  onLoadedIdsChange?: (loadedIds: Set<number>) => void
  isSaving?: boolean
  onEffectiveCountChange?: (count: number) => void
}

const getTagDecorator = (productType: ProductType) =>
  ({
    anomaly: ProductClassTag,
    classification: ProductClassTag,
    segmentation: AnnotationStatusTag,
    classification_patch: AnnotationStatusTag,
  }[productType])

const ObservationsSelectField = ({
  name,
  productType,
  productId,
  trainingId,
  defaultFilters,
  fixedFilters,
  excludeClasses = [],
  excludeTrainingObservations = false,
  useAvailabilityFilter = false,
  showDatasetManager = false,
  setShowDatasetManager,
  dataset,
  onObservationsCreated,
  inTraining = false,
  inTest = false,
  useWindowScroll = true,
  scrollContainerId,
  autoSelectFirst = false,
  initialSelectedObservations = [],
  isEditMode = false,
  inDatasetSection = false,
  onNonAnnotatedImagesInDataset,
  onLoadedIdsChange,
  isSaving = false,
  onEffectiveCountChange,
}: ObservationsSelectFieldProps) => {
  const { product } = useStore()
  const { t } = useTranslation()
  const classes = useStyles()
  const iconClasses = useIconRowStyles()

  const [direction, setDirection] = useState<'desc' | 'asc'>('desc')
  const [order, setOrder] = useState<string[]>([
    'is_used_in_training',
    'creation_date',
  ])

  const [showExplanationModal, setShowExplanationModal] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<(() => void) | null>(null)

  const [percentage, setPercentage] = useState<number>(0)

  const [field, meta, helpers] = useField<Observation[]>(name)

  const { value: fieldValue } = field
  const { touched } = meta
  const { setValue, setTouched } = helpers

  // Safety check: ensure value is always an array
  const value = useMemo(() => (Array.isArray(fieldValue) ? fieldValue : []), [
    fieldValue,
  ])

  const [visibleObservations, setVisibleObservations] = useState<Observation[]>(
    []
  )

  const [hasAutoSelected, setHasAutoSelected] = useState(false)
  // Track which dataset images have been added to selection to avoid duplicates
  const addedDatasetImagesRef = useRef<Set<number>>(new Set())
  // Keep a ref to the current value to avoid stale closures
  const valueRef = useRef<Observation[]>(value)
  const [
    lastSelectedObservation,
    setLastSelectedObservation,
  ] = useState<Observation>()
  const PAGE_SIZE = 40

  const updateSelectedObservations = (observations: Observation[]) => {
    const newObs = observations.filter((o) =>
      value.some((no) => no.id === o.id)
    )
    setValue(newObs)
  }

  // Keep valueRef in sync with current value
  useEffect(() => {
    valueRef.current = value
  }, [value])

  // Reset auto-selection flag and tracking when initialSelectedObservations changes
  useEffect(() => {
    if (initialSelectedObservations.length > 0) {
      setHasAutoSelected(false)
      addedDatasetImagesRef.current = new Set()
    }
  }, [initialSelectedObservations])

  // Auto-select observations based on initialSelectedObservations (only in edit mode)
  // This effect runs whenever visibleObservations changes to handle newly loaded images
  useEffect(() => {
    if (
      autoSelectFirst &&
      visibleObservations.length > 0 &&
      isEditMode &&
      initialSelectedObservations.length > 0
    ) {
      // Create a Set of IDs that should be selected
      const datasetImageIds = new Set(
        initialSelectedObservations.map((obs) => obs.id)
      )

      // Find visible observations that are in the dataset, not used in training,
      // and haven't been processed yet (first time becoming visible)
      const newObservationsToAdd = visibleObservations.filter(
        (vo) =>
          datasetImageIds.has(vo.id) &&
          !vo.isUsedInTraining &&
          !addedDatasetImagesRef.current.has(vo.id)
      )

      // If there are new observations to add, update the selection
      if (newObservationsToAdd.length > 0) {
        // Mark these images as processed (seen)
        newObservationsToAdd.forEach((obs) => {
          addedDatasetImagesRef.current.add(obs.id)
        })

        // Notify parent about loaded IDs change
        if (onLoadedIdsChange) {
          onLoadedIdsChange(new Set(addedDatasetImagesRef.current))
        }

        // Get current selection from ref (always up-to-date)
        const currentValue = valueRef.current
        const currentSelectionIds = new Set(currentValue.map((v) => v.id))

        // Add only the new observations to the current selection
        // This preserves user's manual selections/deselections
        const updatedSelection = [
          ...currentValue,
          ...newObservationsToAdd.filter(
            (obs) => !currentSelectionIds.has(obs.id)
          ),
        ]

        // eslint-disable-next-line no-console
        console.log('Adding newly visible dataset images to selection:', {
          newlyAdded: newObservationsToAdd.length,
          totalTracked: addedDatasetImagesRef.current.size,
          totalDatasetImages: initialSelectedObservations.length,
          updatedSelectionCount: updatedSelection.length,
        })

        setValue(updatedSelection)
      }

      // Mark as auto-selected once we've processed the first batch of visible observations
      // This lifts the loading state even if no dataset images were found in the first page
      if (!hasAutoSelected) {
        setHasAutoSelected(true)
      }
    }
    // Note: 'value' is intentionally not in dependencies to avoid re-running
    // when user manually changes selection. We read it for merging but don't
    // want to trigger on its changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoSelectFirst,
    visibleObservations,
    initialSelectedObservations,
    isEditMode,
    hasAutoSelected,
    setValue,
    onLoadedIdsChange,
  ])

  const handleSelect = (observation: Observation) => {
    setLastSelectedObservation(observation)

    if (isShiftPressed) {
      if (value.length > 0) {
        if (!lastSelectedObservation) {
          return
        }
        const orderField = order.includes('creation_date') ? 'id' : 'filename'

        // Retrieve the selection limits (start and end)
        let startId = lastSelectedObservation[orderField]
        let endId = observation[orderField]
        if (startId < endId) {
          const startIdSaved = startId
          startId = endId
          endId = startIdSaved
        }
        const obsToBeSelected = value
        for (
          let i = visibleObservations.length - 1;
          i >= 0;
          // eslint-disable-next-line no-plusplus
          i--
        ) {
          if (
            startId >= visibleObservations[i][orderField] &&
            visibleObservations[i][orderField] >= endId
          ) {
            const exists = obsToBeSelected.find(
              (item) => item.id === visibleObservations[i].id
            )
            if (!exists && !visibleObservations[i].isUsedInTraining) {
              obsToBeSelected.push(visibleObservations[i])
            }
          }
        }
        setValue(obsToBeSelected)
      }
    } else {
      value.push(observation)
      setValue(value)
    }
  }

  const handleDeselect = (observation: Observation) => {
    setValue(reject((o) => o.id === observation.id, value))
  }

  const handleClick = (observation: Observation) => {
    const isActive = isSelected(observation)

    if (isActive) handleDeselect(observation)
    else handleSelect(observation)
  }

  const isSelected = (observation: Observation): boolean =>
    filter((so) => so.id === observation.id, value).length > 0

  const isExcluded = (observation: Observation): boolean => {
    const isUsedInTraining =
      excludeTrainingObservations &&
      trainingId &&
      observation.trainingIds.includes(trainingId)

    const isClassNotAllowed =
      intersection(excludeClasses, observation.productClasses || []).length > 0

    return !!isUsedInTraining || isClassNotAllowed
  }

  const isDeleteEnabled = (): boolean => {
    if (value.length === 0) return false
    // if at least one selected observation is deletable, return true
    return value.some(
      (o: Observation) =>
        (o.trainingIds?.length || 0) === 0 &&
        (o.trainingTestIds?.length || 0) === 0
    )
  }

  const deletableObsIds = value
    .filter(
      (observation) =>
        (observation.trainingIds?.length || 0) === 0 &&
        (observation.trainingTestIds?.length || 0) === 0
    )
    .map((observation) => observation.id)

  const getDeleteButtonTitle = () => {
    if (deletableObsIds.length < value.length && !isDeleteEnabled())
      return t(
        'All selected images cannot be deleted since they are used in trainings or tests'
      )
    if (deletableObsIds.length < value.length && isDeleteEnabled())
      return t(
        'Some selected images cannot be deleted since they are used in trainings or tests'
      )
    return t('Delete selected images')
  }

  // Check if dataset filter is active from sidebar - disable selection in training or test context
  const hasDatasetSelected =
    (inTraining || inTest) && dataset && dataset.length > 0

  const renderWidgets: ObservationsBrowserWidgets = ({
    params,
    setParams,
    viewMode,
  }) => (
    <>
      {viewMode === 'grid' && (
        <SortModal
          order={order}
          setOrder={setOrder}
          direction={direction}
          setDirection={setDirection}
        />
      )}
      <ObservationsBrowserFilters
        productId={productId}
        trainingId={trainingId}
        annotationType={getAnnotationTypeBy(productType)}
        onSave={(filters) => setParams({ ...params, ...filters })}
        onRemove={() =>
          setParams({
            ...params,
            ...{
              annotationClasses: undefined,
              productClasses: undefined,
              availableForTesting: undefined,
              filename: undefined,
              dataset: undefined,
              datasetExclude: undefined,
            },
          })
        }
        initialValues={{
          annotationClasses: params.annotationClasses,
          productClasses: params.productClasses,
          availableForTesting: params.availableForTesting,
          filename: params.filename,
          dataset: params.dataset,
          datasetExclude: params.datasetExclude,
        }}
        useAvailabilityFilter={useAvailabilityFilter}
        disabled={hasDatasetSelected}
      />
      <SelectAllButton
        selected={value}
        setSelected={setValue}
        params={{ ...params, ...fixedFilters }}
        disabled={hasDatasetSelected}
        showCount={!inDatasetSection}
      />
      {product?.productClasses && product?.productClasses.length > 0 && (
        <SelectPercentageButtonModal
          percentage={percentage}
          setPercentage={setPercentage}
          selected={value}
          setSelected={setValue}
          params={{ ...params, ...fixedFilters }}
          productClasses={product.productClasses}
          productType={productType}
          inTraining={inTraining}
          inTest={inTest}
          disabled={hasDatasetSelected}
        />
      )}
      <DeselectAllButton
        selected={value}
        setSelected={setValue}
        params={{ ...params, ...fixedFilters }}
        disabled={hasDatasetSelected || effectiveSelectedCount === 0}
        showCount={!inDatasetSection}
        onDeselected={(deselected) => {
          setPercentage(0)
          const deselectedIds = new Set(deselected.map((obs) => obs.id))
          initialSelectedObservations.forEach((obs) => {
            if (deselectedIds.has(obs.id)) {
              addedDatasetImagesRef.current.add(obs.id)
            }
          })
          if (onLoadedIdsChange) {
            onLoadedIdsChange(new Set(addedDatasetImagesRef.current))
          }
        }}
      />
      {!inDatasetSection && (
        <DownloadSelectedButton
          selected={value}
          params={{ ...params, ...fixedFilters }}
        />
      )}
      {!inDatasetSection && !hasDatasetSelected && (
        <Button
          onClick={(e) => {
            e.stopPropagation()
            setPendingDelete(() => () => {
              setValue([])
              deleteObservation({
                observationIds: deletableObsIds,
                productId,
              })
              if (deletableObsIds.length < value.length)
                setShowExplanationModal(true)
            })
          }}
          title={getDeleteButtonTitle()}
          icon={<FontAwesomeIcon icon={faTrash} />}
          label={`(${deletableObsIds.length})`}
          hideLabel={!isDeleteEnabled()}
          buttonType="button"
          type="default"
          disabled={!isDeleteEnabled()}
        />
      )}
    </>
  )

  const renderTabularItem = (
    observation: Observation,
    observationsBrowserHelpers: ObservationsBrowserHelpers
  ) => {
    const { openAnnotationModal, openDetailsModal } = observationsBrowserHelpers
    const isActive = isSelected(observation)
    // Disable click if either excluded OR still loading dataset images OR dataset filter is active
    const isClickDisabled =
      isExcluded(observation) || isLoadingDataset || hasDatasetSelected
    // Only show muted style for excluded or loading, not for dataset selection
    const isMuted = isExcluded(observation) || isLoadingDataset
    // Always pass onClick, control execution based on isClickDisabled
    const onClick = isClickDisabled ? undefined : () => handleClick(observation)
    const canAnnotate = !isExcluded(observation) && !isLoadingDataset
    const TagDecorator = getTagDecorator(productType)

    return (
      <BasicTableRow
        key={observation.id}
        rowData={observation}
        onClick={onClick}
        selected={isActive}
        muted={isMuted}
        compact
      >
        <BasicTableCell th>#{observation.id}</BasicTableCell>
        <BasicTableCell>{observation.filename}</BasicTableCell>
        <BasicTableCell>{formatDate(observation.creationDate)}</BasicTableCell>
        <BasicTableCell>
          <div className={iconClasses.iconsRow}>
            <TagDecorator observation={observation} iconOnly />
            {observation.annotationIsPromoted && (
              <FontAwesomeIcon
                icon={faMicrochip}
                title={t('Annotation promoted to ground truth')}
              />
            )}
            {(observation.trainingIds?.length || 0) > 0 && (
              <FontAwesomeIcon
                className={iconClasses.flagIcon}
                icon={faFlag}
                title={titleUndeletableObservation(
                  observation.trainingIds || [],
                  []
                )}
              />
            )}
            {(observation.trainingTestIds?.length || 0) > 0 && (
              <FontAwesomeIcon
                icon={faFlask}
                title={titleUndeletableObservation(
                  [],
                  observation.trainingTestIds || []
                )}
              />
            )}
          </div>
        </BasicTableCell>
        <BasicTableCell>
          <TagDecorator observation={observation} hideIcon />
        </BasicTableCell>
        <BasicTableCell align="right" noWrap>
          <div className={classes.tableActionsCell}>
            <Button
              label={t('Image details')}
              hideLabel
              icon={<FontAwesomeIcon icon={faCircleInfo} />}
              title={t('Info: {{name}}', { name: observation.filename })}
              onClick={(e) => {
                e.stopPropagation()
                openDetailsModal()
              }}
              buttonType="button"
              type="blank-default"
            />
            {!inDatasetSection && !hasDatasetSelected && (
              <Button
                label={
                  (observation.trainingIds?.length || 0) > 0
                    ? t('View image')
                    : t('Annotate classes')
                }
                hideLabel
                icon={
                  <FontAwesomeIcon
                    icon={
                      (observation.trainingIds?.length || 0) > 0
                        ? faEye
                        : faPaintbrush
                    }
                  />
                }
                title={
                  (observation.trainingIds?.length || 0) > 0
                    ? t('View: {{name}}', { name: observation.filename })
                    : t('Annotate classes: {{name}}', {
                        name: observation.filename,
                      })
                }
                onClick={(e) => {
                  e.stopPropagation()
                  openAnnotationModal()
                }}
                buttonType="button"
                type="blank-default"
                disabled={!canAnnotate}
              />
            )}
            {!inDatasetSection && !inTraining && (
              <Button
                label={t('Delete')}
                hideLabel
                icon={<FontAwesomeIcon icon={faTrash} />}
                title={
                  (observation.trainingIds?.length || 0) > 0 ||
                  (observation.trainingTestIds?.length || 0) > 0
                    ? titleUndeletableObservation(
                        observation.trainingIds || [],
                        observation.trainingTestIds || []
                      )
                    : t('Delete: {{name}}', { name: observation.filename })
                }
                onClick={(e) => {
                  e.stopPropagation()
                  setPendingDelete(() => () => {
                    deleteObservation({
                      observationIds: [observation.id],
                      productId,
                    })
                  })
                }}
                buttonType="button"
                type="blank-destroy"
                disabled={
                  (observation.trainingIds?.length || 0) > 0 ||
                  (observation.trainingTestIds?.length || 0) > 0
                }
              />
            )}
          </div>
        </BasicTableCell>
      </BasicTableRow>
    )
  }

  const { mutate: deleteObservation } = useObservationsDelete(
    onObservationsCreated
  )

  // Determine if images should be disabled during initial loading in edit mode or while saving
  const isLoadingDataset =
    (!hasAutoSelected &&
      isEditMode &&
      initialSelectedObservations.length > 0) ||
    isSaving

  const renderGridItem = (
    observation: Observation,
    observationsBrowserHelpers: ObservationsBrowserHelpers
  ) => {
    const { openAnnotationModal, openDetailsModal } = observationsBrowserHelpers
    const isActive = isSelected(observation)
    // Disable click if either excluded OR still loading dataset images OR dataset filter is active
    const isClickDisabled =
      isExcluded(observation) || isLoadingDataset || hasDatasetSelected
    // Only show disabled style for excluded or loading, not for dataset selection
    const isVisuallyDisabled = isExcluded(observation) || isLoadingDataset
    // Always pass onClick, let isClickDisabled control whether it executes
    const onClick = () => handleClick(observation)
    const menuContextualItems = () => {
      const items: MenuItem[] = [
        {
          label: t('Image details'),
          onClick: () => openDetailsModal(),
        },
      ]

      // Show "Annotate classes" / "View image" only when not in dataset section and no dataset is selected
      if (!inDatasetSection && !hasDatasetSelected) {
        items.unshift({
          label:
            (observation.trainingIds?.length || 0) > 0
              ? t('View image')
              : t('Annotate classes'),
          onClick: () => openAnnotationModal(),
        })
      }

      if (!inDatasetSection && !hasDatasetSelected) {
        // Check if observation can be deleted
        const canDelete =
          (observation.trainingIds?.length || 0) === 0 &&
          (observation.trainingTestIds?.length || 0) === 0

        items.push({
          label: t('Delete'),
          type: 'destroy',
          disabled: !canDelete,
          title: !canDelete
            ? titleUndeletableObservation(
                observation.trainingIds || [],
                observation.trainingTestIds || []
              )
            : undefined,
          onClick: () => {
            setPendingDelete(() => () => {
              deleteObservation({
                observationIds: [observation.id],
                productId,
              })
            })
          },
        })
      }

      return items
    }

    return (
      <ObservationItem
        key={observation.id}
        observation={observation}
        onClick={onClick}
        menuContextualItems={menuContextualItems}
        tagDecorator={getTagDecorator(productType)}
        isActive={isActive}
        isDisabled={isVisuallyDisabled}
        isClickDisabled={isClickDisabled}
      />
    )
  }

  useEffect(() => {
    if (!touched) setTouched(value.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, touched])

  const [isShiftPressed, setShiftPressed] = useState(false)

  useEffect(() => {
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Shift') {
        setShiftPressed(true)
      }
    })
    window.addEventListener('keyup', (e) => {
      if (e.key === 'Shift') {
        setShiftPressed(false)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-select images when dataset selection changes
  // For Training: select only annotated images
  // For Test: select only non-annotated images
  useEffect(() => {
    // Only auto-select in training or test context
    if (!inTraining && !inTest) {
      return
    }

    // If dataset is cleared, deselect all observations
    if (!dataset || dataset.length === 0) {
      if (isEditMode) {
        return // Don't clear in edit mode
      }
      setValue([])
      // Reset non-annotated flag when dataset is cleared
      if (onNonAnnotatedImagesInDataset) {
        onNonAnnotatedImagesInDataset(false)
      }
      return
    }

    // Only auto-select if not in edit mode
    if (isEditMode) {
      return
    }

    // Fetch all observations from selected datasets and filter based on context
    const fetchAndSelectObservations = async () => {
      try {
        const queryParams = {
          ...getObservationsQueryParams({
            productId,
            dataset,
            ...defaultFilters,
            ...fixedFilters,
          }),
          offset: 0,
          limit: 20000, // Get all observations
          mode: 'no_image_data', // Lightweight version
        }

        const response = await get<ObservationsResult>(
          `/observations/?${getQueryString(queryParams)}`
        )

        const allObservations = response?.results || []

        // Check for non-annotated images in dataset (for training context)
        if (inTraining && onNonAnnotatedImagesInDataset) {
          const hasNonAnnotated = allObservations.some((obs) => {
            const isAnnotated =
              (obs.productClasses && obs.productClasses.length > 0) ||
              (obs.annotationClasses && obs.annotationClasses !== 'UNDEFINED')
            return !isAnnotated
          })
          onNonAnnotatedImagesInDataset(hasNonAnnotated)
        }

        // Filter observations based on context (training vs test)
        const filteredObservations = allObservations.filter((obs) => {
          const isAnnotated =
            (obs.productClasses && obs.productClasses.length > 0) ||
            (obs.annotationClasses && obs.annotationClasses !== 'UNDEFINED')

          if (inTraining) {
            // For training: select only annotated observations
            return isAnnotated
          }
          if (inTest) {
            // For test: select all observations (both annotated and non-annotated)
            return true
          }
          return false
        })

        // Remove duplicates: if multiple datasets contain the same image,
        // keep only one copy (based on id)
        const uniqueObservationsMap = new Map<number, Observation>()
        filteredObservations.forEach((obs) => {
          if (!uniqueObservationsMap.has(obs.id)) {
            uniqueObservationsMap.set(obs.id, obs)
          }
        })
        const uniqueObservations = Array.from(uniqueObservationsMap.values())

        // Select all unique observations
        if (uniqueObservations.length > 0) {
          setValue(uniqueObservations)
          // eslint-disable-next-line no-console
          console.log(
            `Auto-selected ${uniqueObservations.length} unique ${
              inTraining ? 'annotated' : ''
            } observations from ${dataset.length} dataset(s) (${
              filteredObservations.length
            } total, ${
              filteredObservations.length - uniqueObservations.length
            } duplicates removed)`
          )
        } else {
          // No matching observations found, clear selection
          setValue([])
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching observations:', error)
      }
    }

    fetchAndSelectObservations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset, inTraining, inTest])

  const selectedIds = new Set(value.map((v) => v.id))
  const unloadedDatasetImagesCount = initialSelectedObservations.filter(
    (obs) =>
      !addedDatasetImagesRef.current.has(obs.id) && !selectedIds.has(obs.id)
  ).length
  const effectiveSelectedCount = value.length + unloadedDatasetImagesCount

  useEffect(() => {
    if (onEffectiveCountChange) {
      onEffectiveCountChange(effectiveSelectedCount)
    }
  }, [effectiveSelectedCount, onEffectiveCountChange])

  return (
    <div id="divHeaderImagesId" className={classes.relative}>
      {isLoadingDataset && (
        <div className={classes.loadingToast}>
          <div className={classes.loadingSpinner} />
          <div className={classes.loadingText}>
            {isSaving ? 'Saving dataset...' : 'Loading dataset images...'}
          </div>
        </div>
      )}
      <div className={classes.browserContainer}>
        {isLoadingDataset && <div className={classes.loadingOverlay} />}
        <ObservationsBrowser
          productType={productType}
          productId={productId}
          trainingId={trainingId}
          defaultFilters={defaultFilters}
          widgets={renderWidgets}
          mode={retrieve('observations-browser-mode') || 'grid'}
          onModeChange={(mode) => persist('observations-browser-mode', mode)}
          renderGridItem={renderGridItem}
          renderTabularItem={renderTabularItem}
          setVisibleObservations={setVisibleObservations}
          useWindowScroll={useWindowScroll}
          scrollContainerId={scrollContainerId}
          dataset={dataset}
          order={order}
          direction={direction}
          onObservationsCreated={onObservationsCreated}
          updateSelectedObservations={updateSelectedObservations}
          initialPageSize={PAGE_SIZE}
          uploadDisabled={hasDatasetSelected}
          resetFiltersOnDatasetChange={inTraining || inTest}
          showUploadObservationsButton={!inDatasetSection}
          subHeaderContent={
            inDatasetSection ? (
              <span className={classes.datasetImagesInfo}>
                {t('Selected images')}: {effectiveSelectedCount}
              </span>
            ) : undefined
          }
        />
      </div>
      {pendingDelete && (
        <DeleteConfirmationModal
          onCloseRequest={() => setPendingDelete(null)}
          onDeleteRequest={() => {
            pendingDelete()
            setPendingDelete(null)
          }}
          message={t(
            'Deleting the selected image(s) will also remove them from all datasets that contain them. This action cannot be undone.'
          )}
        />
      )}
      {showExplanationModal && (
        <Modal
          size="sm"
          onCloseRequest={() => setShowExplanationModal(false)}
          title={t('Delete info')}
          footer={
            <Button
              onClick={() => setShowExplanationModal(false)}
              label={t('Close')}
              type="action"
              buttonType="button"
            />
          }
        >
          <div className={classes.modalErrorLineText}>
            {t(
              'It was not possible to delete some selected images because they are used in trainings or tests.'
            )}
          </div>
          <div className={classes.modalErrorLineText}>
            {t(
              'If you want to delete them, please delete first the trainings and tests in which they were used.'
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}

export default ObservationsSelectField
